import map from 'lodash/map';
import filter from 'lodash/filter';
import {
    ASSET_CLEAR_DEVICE_CACHE,
    ASSET_DELETION_FAILED,
    ASSET_DELETION_FINISHED,
    ASSET_DELETION_MODAL_CLOSE,
    ASSET_DELETION_MODAL_OPEN,
    ASSET_DELETION_RESET_STATE,
    ASSET_DELETION_STARTED,
    ASSET_DELETION_SUCCESSFUL,
    ASSET_TAB_CHANGED,
    ASSET_TAG_ADDING_FAILED,
    ASSET_TAG_ADDING_SUCCESSFUL,
    ASSET_TAG_REMOVING_FAILED,
    ASSET_TAG_REMOVING_SUCCESSFUL,
    ASSET_TAGS_UPDATING_FINISHED,
    ASSET_TAGS_UPDATING_RESET_STATE,
    ASSET_TAGS_UPDATING_STARTED,
    ASSET_UPDATE_RESET_STATE,
    ASSET_UPDATING_FAILED,
    ASSET_UPDATING_FINISHED,
    ASSET_UPDATING_STARTED,
    ASSET_UPDATING_SUCCESSFUL,
    AssetDeletionModalCloseAction,
    AssetDeletionModalOpenAction,
    AssetDeletionResetStateAction,
    ASSETS_APPLY_FILTER_CHANGES,
    ASSETS_LOADED,
    ASSETS_LOADED_FAILED,
    ASSETS_SELECTED_ASSOCIATED_DEVICES_CHANGED,
    ASSETS_SELECTED_DEVICES_LOADED,
    ASSETS_SELECTED_DEVICES_LOADED_FAILED,
    ASSETS_START_LOADING,
    ASSETS_START_LOADING_SELECTED_DEVICES,
    ASSETS_TABLE_ROW_SELECTED,
    ASSETS_TABLE_SEARCH_CHANGES,
    ASSETS_TABLE_SORT_CHANGES,
    ASSETS_TABLE_VIEW_TYPE_CHANGED,
    AssetsApplyFilterChangesAction,
    AssetsClearDeviceCacheAction,
    AssetsTabChangedAction,
    AssetsTableViewTypeChangedAction,
    AssetsThunkDispatch,
    AssetTableRowSelectedAction,
    AssetTableSearchChangesAction,
    AssetTableSortChangesAction,
    AssetTagsUpdatingResetStateAction,
    AssetThunkAction,
    AssetUpdateResetStateAction,
    ASSOCIATION_DELETION_FAILED,
    ASSOCIATION_DELETION_FINISHED,
    ASSOCIATION_DELETION_STARTED,
    ASSOCIATION_DELETION_SUCCESSFUL,
    ASSOCIATIONS_LOADED,
    ASSOCIATIONS_LOADED_FAILED,
    ASSOCIATIONS_START_LOADING,
    EASY_ONBOARDING_DIALOG_CLOSE,
    EASY_ONBOARDING_DIALOG_OPEN,
    EasyOnboardingDialogCloseAction,
    EasyOnboardingDialogOpenAction,
    FILTER_DIALOG_CLOSE,
    FILTER_DIALOG_OPEN,
    FilterDialogCloseAction,
    FilterDialogOpenAction,
    IS_ASSETS_RELOAD_IMMINENT,
    IsAssetsReloadImminentAction,
    SELECTED_ASSET_UPDATE,
} from './types';
import {
    AssetAssociation,
    AssetAssociationWithDevice,
    DeviceType,
    SelectedTag,
    SelectedTagStatus,
} from '../../components/assets/details/types';
import { Asset, AssetStatus, AssetWithEmbed, AssociatedDevice, SortDirection } from '../../components/assets/types';
import { AccessToken, getAccessToken, reportErrorToSentry } from '../../../../configuration';
import { config } from '../../../../config';
import { getErrorCode, sleep } from '../helper';
import { AssetsListDetailTab } from '../../containers/assets/details/types';
import {
    closableSuccessFormattedNotification,
    nonVanishingFormattedErrorNotification,
} from '../../components/assets/ClickableNotifications';
import { TableViewTogglesValues } from '@rio-cloud/rio-uikit/lib/es/types';
import { State } from '../../../../types';

export function fetchAssets(): AssetThunkAction<Promise<void>> {
    return async (dispatch: AssetsThunkDispatch, getState: () => State) => {
        const state: State = getState();
        dispatch({ type: ASSETS_START_LOADING });

        await fetchAssetsPaginated(getAccessToken(state))
            .then((result: Asset[]) =>
                dispatch({
                    type: ASSETS_LOADED,
                    payload: result,
                })
            )
            .catch(() =>
                dispatch({
                    type: ASSETS_LOADED_FAILED,
                })
            );
    };
}

async function fetchAssetsPaginated(accessToken: AccessToken): Promise<Asset[]> {
    const assetsEndpoint = `${config.backend.assetAdmin}/assets?embed=(tags,master_data)`;
    let nextLink: string | undefined = assetsEndpoint;
    let assets: Asset[] = [];

    while (nextLink !== undefined) {
        const response: any = await fetch(nextLink, { headers: { Authorization: `Bearer ${accessToken}` } });
        const contentType = response.headers.get('content-type');
        if (response.status === 200 && contentType && contentType.indexOf('application/json') !== -1) {
            const json = await response.json();
            nextLink = json._links?.next?.href;
            assets = assets.concat(mapToAssets(json.items));
        } else {
            throw new Error(response);
        }
    }
    return assets;
}

function mapToAssets(items: AssetWithEmbed[]): Asset[] {
    return items.map((assetWithEmbed: AssetWithEmbed) => {
        return {
            id: assetWithEmbed.id,
            account_id: assetWithEmbed.account_id,
            name: assetWithEmbed.name,
            identification: assetWithEmbed.identification,
            identification_type: assetWithEmbed.identification_type,
            type: assetWithEmbed.type,
            status: assetWithEmbed.status,
            brand: assetWithEmbed.brand,
            license_plate: assetWithEmbed.license_plate,
            license_plate_country_code: assetWithEmbed.license_plate_country_code,
            tags: assetWithEmbed?._embedded?.tags?.items?.map((item: { id: string }) => item.id) ?? [],
            masterdata: assetWithEmbed?._embedded?.master_data ?? null,
        };
    });
}

export function updateAsset(originalAsset: Asset, updatedAsset: Asset): AssetThunkAction<Promise<void>> {
    return async (dispatch: AssetsThunkDispatch, getState: () => State) => {
        const state: State = getState();
        if (hasAssetChanges(updatedAsset, originalAsset)) {
            dispatch({
                type: ASSET_UPDATING_STARTED,
            });
            const assetEndpoint = `${config.backend.assetAdmin}/assets/${updatedAsset.id}`;
            const updateAssetExternal: AssetWithEmbed = {
                id: updatedAsset.id,
                account_id: updatedAsset.account_id,
                name: updatedAsset.name,
                identification: updatedAsset.identification,
                identification_type: updatedAsset.identification_type,
                type: updatedAsset.type,
                status: updatedAsset.status,
                brand: updatedAsset.brand,
                license_plate: updatedAsset.license_plate,
                license_plate_country_code: updatedAsset.license_plate_country_code,
            };
            const response = await fetch(assetEndpoint, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${getAccessToken(state)}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updateAssetExternal),
            });
            if (response.status === 202) {
                dispatch({
                    type: SELECTED_ASSET_UPDATE,
                    payload: {
                        assetId: updatedAsset.id,
                        name: updatedAsset.name,
                        license_plate: updatedAsset.license_plate,
                        license_plate_country_code: updatedAsset.license_plate_country_code,
                        status: updatedAsset.status,
                        brand: updatedAsset.brand,
                        identification_type: updatedAsset.identification_type,
                        identification: updatedAsset.identification,
                    },
                });
                if (originalAsset.status === AssetStatus.active && updatedAsset.status === AssetStatus.archived) {
                    setTimeout(() => dispatch(clearDeviceCache(updatedAsset.id)), 1000);
                }
                dispatch(assetTableRowSelected(updatedAsset));
                //
                dispatch({
                    type: ASSET_UPDATING_SUCCESSFUL,
                });
            } else {
                const errorCode = await getErrorCode(response);
                //
                dispatch({
                    type: ASSET_UPDATING_FAILED,
                    payload: {
                        errorCode,
                    },
                });
            }
            dispatch({
                type: ASSET_UPDATING_FINISHED,
            });
        }
    };
}

const hasAssetChanges = (updatedAsset: Asset, originalAsset: Asset) => {
    return (
        updatedAsset.name !== originalAsset.name ||
        (originalAsset.license_plate
            ? updatedAsset.license_plate !== originalAsset.license_plate
            : updatedAsset.license_plate && updatedAsset.license_plate.length > 0) ||
        updatedAsset.license_plate_country_code !== originalAsset.license_plate_country_code ||
        updatedAsset.status !== originalAsset.status ||
        (originalAsset.identification ? false : updatedAsset.identification !== null && updatedAsset.brand !== null)
    );
};

export function assetDeleteDialogOpen(): AssetDeletionModalOpenAction {
    return {
        type: ASSET_DELETION_MODAL_OPEN,
    };
}

export function assetDeleteDialogClose(): AssetDeletionModalCloseAction {
    return {
        type: ASSET_DELETION_MODAL_CLOSE,
    };
}

export function deleteAsset(asset: Asset): AssetThunkAction<Promise<void>> {
    return async (dispatch: AssetsThunkDispatch, getState: () => State) => {
        const state: State = getState();
        dispatch({
            type: ASSET_DELETION_STARTED,
        });
        const assetEndpoint = `${config.backend.assetAdmin}/assets/${asset.id}`;
        const response = await fetch(assetEndpoint, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${getAccessToken(state)}`,
            },
        });
        if (response.status === 202) {
            //
            dispatch({
                type: ASSET_DELETION_SUCCESSFUL,
                payload: {
                    assetId: asset.id,
                },
            });
        } else {
            //
            dispatch({
                type: ASSET_DELETION_FAILED,
            });
        }
        dispatch({
            type: ASSET_DELETION_FINISHED,
        });
    };
}

export function assetTableViewTypeChanges(viewType: TableViewTogglesValues): AssetsTableViewTypeChangedAction {
    return {
        type: ASSETS_TABLE_VIEW_TYPE_CHANGED,
        payload: viewType,
    };
}

export function assetTableRowSelected(asset: Asset | null): AssetTableRowSelectedAction {
    return {
        type: ASSETS_TABLE_ROW_SELECTED,
        payload: asset,
    };
}

export function assetTableSortChanges(column: string, dir: SortDirection): AssetTableSortChangesAction {
    return {
        type: ASSETS_TABLE_SORT_CHANGES,
        payload: {
            sortBy: column,
            sortDir: dir,
        },
    };
}

export function assetTableSearchChanged(searchString: string): AssetTableSearchChangesAction {
    return {
        type: ASSETS_TABLE_SEARCH_CHANGES,
        payload: {
            searchString,
        },
    };
}

export function assetTableApplyFilterChanges(
    filterActive: boolean,
    showActiveAssets: boolean,
    showArchivedAssets: boolean
): AssetsApplyFilterChangesAction {
    return {
        type: ASSETS_APPLY_FILTER_CHANGES,
        payload: {
            showOnlyAssetsWithoutDataSources: filterActive,
            showActiveAssets,
            showArchivedAssets,
        },
    };
}

export function fetchDevices(): AssetThunkAction<Promise<void>> {
    return async (dispatch: AssetsThunkDispatch, getState: () => State) => {
        const state: State = getState();
        dispatch({
            type: ASSOCIATIONS_START_LOADING,
        });
        const associationsEndpoint = `${config.backend.assetAdmin}/associations?embed=(device)`;
        const response = await fetch(associationsEndpoint, {
            headers: {
                Authorization: `Bearer ${getAccessToken(state)}`,
            },
        });
        const contentType = response.headers.get('content-type');

        if (response.status === 200 && contentType && contentType.indexOf('application/json') !== -1) {
            const json = await response.json();
            const associationsWithDevices: AssetAssociationWithDevice[] = json.items;
            dispatch({
                type: ASSOCIATIONS_LOADED,
                payload: associationsWithDevices,
            });
        } else {
            dispatch({
                type: ASSOCIATIONS_LOADED_FAILED,
            });
        }
    };
}

export function fetchSelectedDevices(
    assetId: string,
    associatedDevices: { [p: string]: AssociatedDevice[] }
): (dispatch: AssetsThunkDispatch, getState: () => State) => Promise<void> {
    return async (dispatch: AssetsThunkDispatch, getState: () => State) => {
        const state: State = getState();
        // eslint-disable-next-line no-prototype-builtins
        if (associatedDevices.hasOwnProperty(assetId)) {
            dispatch({
                type: ASSETS_SELECTED_ASSOCIATED_DEVICES_CHANGED, // TODO: split selection of devices in separate action
                payload: {
                    selectedAssociatedDevices: associatedDevices[assetId],
                },
            });
        } else {
            dispatch({
                type: ASSETS_START_LOADING_SELECTED_DEVICES,
            });
            const url = new URL(`${config.backend.assetAdmin}/associations`);
            url.search = new URLSearchParams({ asset_id: assetId }).toString();
            const rawUrlWithEmbedParameter = `${url.toString()}&embed=(device)`;

            const response = await fetch(rawUrlWithEmbedParameter, {
                headers: {
                    Authorization: `Bearer ${getAccessToken(state)}`,
                },
            });
            const contentType = response.headers.get('content-type');

            if (response.status === 200 && contentType && contentType.indexOf('application/json') !== -1) {
                const json = await response.json();
                const filteredResults: AssetAssociationWithDevice[] = filter(
                    json.items,
                    (association: AssetAssociationWithDevice) => association._embedded !== undefined
                );
                const connectedDevices: AssociatedDevice[] = map(
                    filteredResults,
                    (association: AssetAssociationWithDevice) => {
                        return {
                            device: association._embedded.device,
                            associationId: association.id,
                        };
                    }
                );
                dispatch({
                    type: ASSETS_SELECTED_DEVICES_LOADED,
                    payload: {
                        assetId,
                        associatedDevices: connectedDevices,
                    },
                });
            } else {
                dispatch({
                    type: ASSETS_SELECTED_DEVICES_LOADED_FAILED,
                });
            }
        }
    };
}

export function setIsAssetReloadImminent(isImminent: boolean): IsAssetsReloadImminentAction {
    return {
        type: IS_ASSETS_RELOAD_IMMINENT,
        payload: isImminent,
    };
}
export function closeEasyOnboardingDialog(): EasyOnboardingDialogCloseAction {
    return {
        type: EASY_ONBOARDING_DIALOG_CLOSE,
    };
}
export function openEasyOnboardingDialog(
    selectedAssetId: string | undefined = undefined
): EasyOnboardingDialogOpenAction {
    return {
        type: EASY_ONBOARDING_DIALOG_OPEN,
        payload: {
            selectedAssetId: selectedAssetId,
        },
    };
}

export function clearDeviceCache(assetId: string): AssetsClearDeviceCacheAction {
    return {
        type: ASSET_CLEAR_DEVICE_CACHE,
        payload: assetId,
    };
}

export function deleteAssociation(
    association: AssetAssociation,
    deviceType: DeviceType
): AssetThunkAction<Promise<void>> {
    return async (dispatch: AssetsThunkDispatch, getState: () => State) => {
        const state: State = getState();
        dispatch({
            type: ASSOCIATION_DELETION_STARTED,
        });
        const associationEndpoint = `${config.backend.assetAdmin}/associations/${association.id}`;
        const response = await fetch(associationEndpoint, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${getAccessToken(state)}`,
            },
        });
        if (response.status === 202) {
            dispatch({
                type: ASSOCIATION_DELETION_SUCCESSFUL,
                payload: {
                    associationId: association.id,
                },
            });
            closableSuccessFormattedNotification('assets.assets.delete.success');
        } else {
            dispatch({
                type: ASSOCIATION_DELETION_FAILED,
            });
            const errorCode = await getErrorCode(response);
            nonVanishingFormattedErrorNotification(
                'assets.assets.asset.delete-confirmation-dialog.content.error.DEFAULT',
                `${errorCode}`
            );
        }
        dispatch({
            type: ASSOCIATION_DELETION_FINISHED,
        });
    };
}

export function updateAssetTags(asset: Asset, tags: SelectedTag[]): AssetThunkAction<Promise<void>> {
    const assetTagsBaseEndpoint = `${config.backend.assetAdmin}/assets/${asset.id}/tags`;

    async function putRequest(tagToAdd: SelectedTag, accessToken: AccessToken) {
        const assetTagsEndpoint = `${assetTagsBaseEndpoint}/${tagToAdd.id}`;
        return fetch(assetTagsEndpoint, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: tagToAdd.id,
            }),
        });
    }

    async function deleteRequest(tagToRemove: SelectedTag, accessToken: AccessToken) {
        const assetTagsEndpoint = `${assetTagsBaseEndpoint}/${tagToRemove.id}`;
        return fetch(assetTagsEndpoint, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        });
    }

    return async (dispatch: AssetsThunkDispatch, getState: () => State) => {
        const state: State = getState();
        dispatch({
            type: ASSET_TAGS_UPDATING_STARTED,
        });
        const tagsToRemove = tags.filter((tag) => tag.status === SelectedTagStatus.removed);
        const tagsToAdd = tags.filter((tag) => tag.status === SelectedTagStatus.added);
        const createdTagsToAdd = tags.filter((tag) => tag.status === SelectedTagStatus.created);

        for (const tagToRemove of tagsToRemove) {
            const response = await deleteRequest(tagToRemove, getAccessToken(state));
            if (response.status === 202) {
                dispatch({
                    type: ASSET_TAG_REMOVING_SUCCESSFUL,
                    payload: {
                        tag: tagToRemove,
                        asset,
                    },
                });
            } else {
                dispatch({
                    type: ASSET_TAG_REMOVING_FAILED,
                    payload: {
                        tag: tagToRemove,
                        asset,
                    },
                });
            }
        }

        for (const tagToAdd of tagsToAdd) {
            const response = await putRequest(tagToAdd, getAccessToken(state));
            if (response.status === 202) {
                dispatch({
                    type: ASSET_TAG_ADDING_SUCCESSFUL,
                    payload: {
                        tag: tagToAdd,
                        asset,
                    },
                });
            } else {
                dispatch({
                    type: ASSET_TAG_ADDING_FAILED,
                    payload: {
                        tag: tagToAdd,
                        asset,
                    },
                });
            }
        }

        for (const createdTagToAdd of createdTagsToAdd) {
            for (let retryCount = 0; retryCount < config.actions.assets.maximumCreateTagRetries; retryCount++) {
                const response = await putRequest(createdTagToAdd, getAccessToken(state));
                if (response.status === 202) {
                    //
                    dispatch({
                        type: ASSET_TAG_ADDING_SUCCESSFUL,
                        payload: {
                            tag: createdTagToAdd,
                            asset,
                        },
                    });
                    break;
                }
                await sleep(config.actions.assets.sleepBetweenCreateTagRetriesMs);
                if (retryCount === config.actions.assets.maximumCreateTagRetries - 1) {
                    reportErrorToSentry(
                        new Error(`Could not add tag ${createdTagToAdd.id} to asset ${asset.id}
                        after ${config.actions.assets.maximumCreateTagRetries} retries`)
                    );
                    //
                    dispatch({
                        type: ASSET_TAG_ADDING_FAILED,
                        payload: {
                            tag: createdTagToAdd,
                            asset,
                        },
                    });
                }
            }
        }

        dispatch({
            type: ASSET_TAGS_UPDATING_FINISHED,
        });
    };
}

export function changeAssetDetailsTab(selectedTab: AssetsListDetailTab): AssetsTabChangedAction {
    return {
        type: ASSET_TAB_CHANGED,
        payload: {
            selectedTab,
        },
    };
}

export function filterOptionsDialogClose(): FilterDialogCloseAction {
    return {
        type: FILTER_DIALOG_CLOSE,
    };
}

export function filterOptionsDialogOpen(): FilterDialogOpenAction {
    return {
        type: FILTER_DIALOG_OPEN,
    };
}

export const resetAssetTagsUpdatingState = (): AssetTagsUpdatingResetStateAction => ({
    type: ASSET_TAGS_UPDATING_RESET_STATE,
});

export const resetAssetDeletionState = (): AssetDeletionResetStateAction => ({
    type: ASSET_DELETION_RESET_STATE,
});

export const resetAssetUpdateState = (): AssetUpdateResetStateAction => ({
    type: ASSET_UPDATE_RESET_STATE,
});
